import React, { useMemo, useRef, useCallback } from "react";
import { useTranslate } from "hooks";
import { Icon } from "components";
import { UncontrolledTooltip } from "reactstrap";
import type { OrderCodePart as OrderCodePartType } from "stores/product";
import {
  ConfiguratorTabs,
  useProductOutput,
  useSetActivePartKey,
  useActiveTab,
  useIsWizardProduct,
  useProductNodeId,
} from "stores/product";
import { OrderCodePart } from "./OrderCodePart";
import { SaveChangesButton } from "./SaveChangesButton";
import { RestoreProductButton } from "./RestoreProductButton";
import NiceModal from "@ebay/nice-modal-react";
import { ProductConfiguratorModal } from "components/_Modals/ProductConfiguratorModal";

export const OrderCode = ({ isModal = false }: { isModal?: boolean }) => {
  const allOptionsTooltipRef = useRef<HTMLButtonElement>(null);
  const translate = useTranslate();
  const productOutput = useProductOutput();
  const setActivePartKey = useSetActivePartKey();
  const activeTab = useActiveTab();
  const isWizardProduct = useIsWizardProduct();
  const nodeId = useProductNodeId();

  const presentationParts = useMemo(
    () =>
      productOutput?.product?.orderCode?.parts?.filter(
        (part) =>
          // 0 - RelevantChangeable; 1 - RelevantNotChangeable
          // https://dev.azure.com/DEVLY-IT-Software-Development/TROX-EB-Website_v2/_wiki/wikis/TROX-System-Configurator.wiki/1132/Product-states
          part?.state === 0 ||
          part?.state === 1 ||
          (part?.state === 3 && part?.presentation),
      ) || [],
    [productOutput],
  );

  const openConfigurationModal = useCallback(() => {
    setActivePartKey(null);
    NiceModal.show(ProductConfiguratorModal);
  }, [setActivePartKey]);

  const onPartClick = useCallback(
    (part: OrderCodePartType) => {
      if (!isModal && activeTab !== ConfiguratorTabs.PRODUCT_SPECIFICATION) {
        setActivePartKey(part.key);
        NiceModal.show(ProductConfiguratorModal);
        return;
      }
      if (isModal) {
        const modalPartElement = document.getElementById(
          `modal-part-${part.key}`,
        );
        modalPartElement?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else {
        const partElement = document.getElementById(`part-${part.key}`);
        partElement?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setActivePartKey(part.key);
    },
    [activeTab, isModal, setActivePartKey],
  );

  return (
    <div className="d-flex flex-wrap align-items-center gap-1">
      {!isModal && (
        <>
          <button
            className="d-flex justify-content-center align-items-center p-0 bg-transparent border-0"
            ref={allOptionsTooltipRef}
            data-testid="product-configurator-header-open-modal-button"
          >
            <Icon
              iconName="squares-regular"
              id="product-configurator-grid-2-icon"
              className="icon-20 text-gray-700 cursor-pointer"
              // Todo: add onClick to open order code options modal
              onClick={openConfigurationModal}
            />
          </button>
          <UncontrolledTooltip
            placement="top"
            target={allOptionsTooltipRef as React.RefObject<HTMLButtonElement>}
          >
            {translate("PRODUCT_CONFIGURATOR_SHOW_ALL_OPTIONS_BUTTON_TOOLTIP")}
          </UncontrolledTooltip>
        </>
      )}
      {presentationParts.map(
        (part) =>
          part && (
            <OrderCodePart
              key={part?.key}
              part={part}
              onClick={() => onPartClick(part)}
            />
          ),
      )}
      <div className="d-flex gap-2">
        {!isWizardProduct && nodeId && <SaveChangesButton />}
        <RestoreProductButton />
      </div>
    </div>
  );
};
